import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {useHistory} from 'react-router-dom'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {useAuth} from '../authentication/AuthProvider'
import GenericPageError from '../error/GenericPageError'
import PageContentWrapper from '../layout/PageContentWrapper'
import {tableToolbarType} from '../styled-component/Constant'
import {DatatableContext} from '../styled-component/DataTableContext'
import StyledDataTableWrapper from '../styled-component/StyledDataTableWrapper'
import {tableTypes} from '../styled-component/StyledTableUtils'
import {
    filterDataTableEvent,
    filterEventTypes,
    getFilterEventKey,
    removeFilterChangeListener,
} from '../styled-component/broadcast/FilterEvent'
import {columnList} from './ClientConstants'
import {getDetailClient, getPageClients} from './ClientEndPoint'
import ClientForm from './ClientForm'
import {
    clientEvent,
    clientEventTypes,
    getClientEventKey,
    onClientTableChangeRemoveListener,
} from './broadcast/ClientEvent'
import {usePageClients} from './query-keys/ClientQueryManager'

const ClientDataTable = () => {
    useSignals()
    const queryClient = useQueryClient()

    //#region Import
    const {user} = useAuth()
    const history = useHistory()
    //#endregion

    //#region Ref
    const formRef = useRef(null)
    //#endregion

    //#region State
    //#region State Controls
    const pageable = useSignal({
        pageRows: 10,
        pageNumber: 0,
        sortField: 'createdAt',
        sortOrder: -1,
    })

    const filterRequest = useSignal({
        globalSearchValue: null,
    })

    // Pageable
    const handleChangeSortColumn = useCallback(
        e => {
            pageable.value = {
                ...pageable.value,
                sortField: e.sortField,
                sortOrder: e.sortOrder,
            }
        },
        [pageable.value.sortField, pageable.value.sortOrder],
    )

    const handleChangePageNumber = useCallback(
        newPageNumber => {
            pageable.value = {
                ...pageable.value,
                pageNumber: newPageNumber,
            }

            handlePrefetchDataByPage(newPageNumber + 1)
        },
        [pageable.value.pageNumber],
    )

    const handleChangePageRows = useCallback(
        newPageRows => {
            pageable.value = {
                ...pageable.value,
                pageRows: newPageRows,
                pageNumber: 0,
            }

            pagePrefetchRef.current = [0]

            handlePrefetchDataByPage(1)
        },
        [pageable.value.pageRows],
    )

    // Global Search Value
    const handleChangeGlobalSearch = useCallback(value => {
        filterRequest.value = {
            ...filterRequest.value,
            globalSearchValue: value || null,
        }
    }, [])

    // Filter Request
    const handleClearFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: null,
        }
    }, [])

    const handleClearFilterRequestByKey = useCallback((key, value) => {
        filterRequest.value = {
            ...filterRequest.value,
            [key]: value,
        }
    }, [])

    const handleClearAdvancedFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: filterRequest.value?.globalSearchValue || null,
        }
    }, [])

    //#region Query
    const getCurrentQueryKey = ({newPageNumber, newPageRows}) => {
        const {clientKeys} = QueryKeyManager(QueryKeyTypes.client, user?.id)

        return clientKeys.page({
            pageNumber:
                newPageNumber !== null && typeof newPageNumber === 'number'
                    ? newPageNumber
                    : pageable.value.pageNumber,
            pageRows:
                newPageRows !== null && typeof newPageNumber === 'number'
                    ? newPageRows
                    : pageable.value.pageRows,
            sortField: pageable.value.sortField,
            sortOrder: pageable.value.sortOrder,
            filterRequest: {
                ...filterRequest.value,
            },
        })
    }

    const {data: queryData, isFetched} = usePageClients(
        user?.id,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        filterRequest.value,
        true,
    )
    //#endregion

    const dataList = useRef([])
    const currentDataList = useMemo(() => {
        dataList.current = queryData?.content || []

        return queryData?.content || []
    }, [queryData?.content])

    // Page Rows from Pagination
    const rowsOptionHoveredRef = useRef([10])
    const handlePrefetchDataByRowsOption = useCallback(rows => {
        if (!!user?.id && !rowsOptionHoveredRef.current?.includes(rows)) {
            rowsOptionHoveredRef.current.push(rows)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: 0,
                    newPageRows: rows,
                }),
                queryFn: () =>
                    getPageClients(
                        user?.id,
                        0,
                        rows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    const detailRowHoveredRef = useRef([])
    const handlePrefetchDataByDetailRow = useCallback(id => {
        if (!!user?.id && !detailRowHoveredRef.current?.includes(id) && !!id) {
            detailRowHoveredRef.current.push(id)

            const {clientKeys} = QueryKeyManager(QueryKeyTypes.client, user?.id)

            queryClient.prefetchQuery({
                queryKey: clientKeys.detail({id}),
                queryFn: () => getDetailClient(user?.id, id),
            })
        }
    }, [])

    const handleOnDetailRowClick = id => {
        if (!!id) {
            history.push(`/clients/${id}`)
        }
    }

    // Page Number from Pagination
    const pagePrefetchRef = useRef([0])
    const handlePrefetchDataByPage = useCallback(pageNumber => {
        if (!!user?.id && !pagePrefetchRef.current?.includes(pageNumber)) {
            pagePrefetchRef.current.push(pageNumber)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: pageNumber,
                    newPageRows: pageable.peek().pageRows,
                }),
                queryFn: () =>
                    getPageClients(
                        user?.id,
                        pageNumber,
                        pageable.peek().pageRows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])
    //#endregion

    //#region Event Listener on State Changes
    useEffect(() => {
        clientEvent.on(
            `${getClientEventKey(clientEventTypes.TABLE_CHANGE)}`,
            clientRecord => {
                if (!clientRecord || !clientRecord?.id) {
                    return
                }

                const {clientKeys} = QueryKeyManager(
                    QueryKeyTypes.client,
                    user?.id,
                )
                queryClient.invalidateQueries({
                    queryKey: clientKeys.pattern(),
                })
            },
        )

        handlePrefetchDataByPage(pageable.value.pageNumber + 1)

        filterDataTableEvent.on(
            `${getFilterEventKey(
                tableTypes.client,
                filterEventTypes.advanced,
            )}`,
            newFilterConfig => {
                if (!newFilterConfig || typeof newFilterConfig !== 'object') {
                    return
                }

                if (Object.keys(newFilterConfig).length === 0) {
                    handleClearAdvancedFilterRequest()

                    return
                }

                filterRequest.value = {
                    ...filterRequest.value,
                    ...newFilterConfig,
                }
            },
        )

        return () => {
            onClientTableChangeRemoveListener(clientEventTypes.TABLE_CHANGE)

            removeFilterChangeListener(
                tableTypes.client,
                filterEventTypes.advanced,
            )
        }
    }, [])
    //#endregion

    //#region Handle Function
    const handleAddNew = useCallback(() => {
        formRef.current?.showForm()
    }, [])
    //#endregion

    return (
        <PageContentWrapper>
            <DatatableContext.Provider
                value={{
                    dataKey: 'id',
                    tableType: tableTypes.client,
                    queryData: queryData,
                    isFetched: isFetched,
                    columnList: columnList,
                    tableToolbar: [tableToolbarType.tableControlToolbar],

                    pageable: pageable.value,
                    onChangeSortColumn: handleChangeSortColumn,
                    onChangePageNumber: handleChangePageNumber,
                    onChangePageRows: handleChangePageRows,
                    onPageRowsHovered: handlePrefetchDataByRowsOption,

                    onDetailRowHovered: handlePrefetchDataByDetailRow,
                    onDetailRowClick: handleOnDetailRowClick,

                    handleClearFilterRequest: handleClearFilterRequest,
                    handleClearFilterRequestByKey:
                        handleClearFilterRequestByKey,
                    handleClearAdvancedFilterRequest:
                        handleClearAdvancedFilterRequest,

                    handleChangeGlobalSearch: handleChangeGlobalSearch,

                    handleAddNew: handleAddNew,
                }}>
                <StyledDataTableWrapper
                    headerOptions={{
                        title: 'Client Management',
                        subTitle:
                            'To manage top-level information about clients and their respective data and configurations.',
                    }}
                />
                <GenericPageError />
                <ClientForm ref={formRef} />
            </DatatableContext.Provider>
        </PageContentWrapper>
    )
}

export default ClientDataTable
